import { parseDbTimestamp } from "@repo/shared";
import { KeyRound, Smartphone, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { OAuthApp } from "@/lib/api/security";
import { useOAuthApps, useRevokeOAuthApp } from "@/lib/hooks/use-security";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./oauth-apps-section.i18n";

export function OAuthAppsSection() {
  const { formatDate: formatRegionalDate } = useRegionalFormat();
  const t = useT(T);
  const { apps, isLoading } = useOAuthApps();
  const revokeMutation = useRevokeOAuthApp();
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [appToRevoke, setAppToRevoke] = useState<OAuthApp | null>(null);

  const handleRevoke = (app: OAuthApp) => {
    setAppToRevoke(app);
    setRevokeDialogOpen(true);
  };

  const confirmRevoke = () => {
    if (!appToRevoke) return;
    revokeMutation.mutate(appToRevoke.id, {
      onSettled: () => {
        setRevokeDialogOpen(false);
        setAppToRevoke(null);
      },
    });
  };

  const formatDate = (date: string | null) => {
    if (!date) return "-";
    return formatRegionalDate(parseDbTimestamp(date));
  };

  const getAppIcon = (app: OAuthApp) => {
    // Check if app name suggests it's Claude
    const name = app.name?.toLowerCase() || "";
    if (name.includes("claude")) {
      return <Smartphone className="h-5 w-5 text-muted-foreground" />;
    }
    return <KeyRound className="h-5 w-5 text-muted-foreground" />;
  };

  const getAppName = (app: OAuthApp) => {
    return app.name || app.clientId || "Unknown Application";
  };

  const formatScopes = (scopes: string | null) => {
    if (!scopes) return t.fullAccess;
    return scopes.split(" ").join(", ");
  };

  return (
    <>
      <div className="space-y-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <KeyRound className="h-5 w-5" />
            {t.title}
          </h2>
          <p className="text-sm text-muted-foreground">{t.description}</p>
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground py-4">{t.loading}</p>
        ) : apps.length === 0 ? (
          <EmptyState>
            <KeyRound className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <span className="block font-medium text-foreground">{t.noApps}</span>
            <span className="block text-sm mt-1">{t.noAppsDescription}</span>
          </EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.application}</TableHead>
                <TableHead>{t.scopes}</TableHead>
                <TableHead>{t.authorizedOn}</TableHead>
                <TableActionsHead>{t.actions}</TableActionsHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apps.map((app) => (
                <TableRow key={app.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {getAppIcon(app)}
                      <span className="font-medium">{getAppName(app)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatScopes(app.scopes)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(app.createdAt)}
                  </TableCell>
                  <TableActionsCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRevoke(app)}
                      disabled={revokeMutation.isPending}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      tooltip={t.revoke}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableActionsCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.revokeTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.revokeDescription}
              {appToRevoke && (
                <span className="block mt-2 font-medium text-foreground">
                  {getAppName(appToRevoke)}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revokeMutation.isPending}>{t.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRevoke}
              disabled={revokeMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t.revokeConfirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
